import { type InteractionType } from '@components/Presentation/types';
import { shuffle } from 'lodash';

export interface QuestionSuggestion {
  feature: InteractionType;
  audience: string;
  question: string;
  setting?: {
    ratingPollScale?: '5' | '10';
    lowScoreLabel?: string;
    highScoreLabel?: string;
  };
  options?: { body: string; checked: boolean; option_number: number }[];
}

export const questionSuggestions: QuestionSuggestion[] = [
  {
    feature: 'map',
    audience: 'Generic',
    question: 'What city are you joining from today? Type it in the chat.',
  },
  {
    feature: 'map',
    audience: 'Generic',
    question: 'What city did you spend most of your working-life in?',
  },
  {
    feature: 'map',
    audience: 'Generic',
    question: 'Where would you love to travel next?',
  },
  {
    feature: 'map',
    audience: 'Corporates',
    question:
      'Drop a pin on the city that hosts our most successful competitor\'s main office.',
  },
  {
    feature: 'map',
    audience: 'Corporates',
    question:
      'Pinpoint a city where we should focus our next sales outreach campaign.',
  },
  {
    feature: 'map',
    audience: 'Corporates',
    question: 'Mark the location of a recent client success story on the map.',
  },

  {
    feature: 'map',
    audience: 'Classes',
    question:
      'Pinpoint the country that is home to your favorite data science team or company.',
  },
  {
    feature: 'map',
    audience: 'Classes',
    question:
      'Mark a city on the map where you\'d be excited to work in this industry.',
  },
  {
    feature: 'map',
    audience: 'Classes',
    question:
      'Place a pin on the location of a key innovation or invention from our course material.',
  },

  {
    feature: 'map',
    audience: 'Creatives',
    question:
      'Pinpoint the country that most influences your current artistic style.',
  },
  {
    feature: 'map',
    audience: 'Creatives',
    question:
      'Drop a pin on a city or gallery you hope to exhibit your work in one day.',
  },
  {
    feature: 'map',
    audience: 'Creatives',
    question:
      'Mark the location of your favorite fictional setting that inspires your work.',
  },

  {
    feature: 'wordCloud',
    audience: 'Generic',
    question: 'Name a colleague you are grateful to.',
  },
  {
    feature: 'wordCloud',
    audience: 'Generic',
    question: 'What’s your go-to comfort food?',
  },
  {
    feature: 'wordCloud',
    audience: 'Generic',
    question: 'What\'s your favorite AI tool?',
  },
  {
    feature: 'wordCloud',
    audience: 'Corporates',
    question:
      'In one word, what\'s the biggest challenge you see in our current market?',
  },
  {
    feature: 'wordCloud',
    audience: 'Corporates',
    question: 'Summarize our team\'s Q2 performance in a single word.',
  },
  {
    feature: 'wordCloud',
    audience: 'Corporates',
    question:
      'What\'s one word to describe the key value of our new product feature?',
  },

  {
    feature: 'wordCloud',
    audience: 'Classes',
    question:
      'In one word, what\'s the most valuable skill you\'ve learned in this course so far?',
  },
  {
    feature: 'wordCloud',
    audience: 'Classes',
    question:
      'Describe the biggest hurdle in your last project in a single word.',
  },
  {
    feature: 'wordCloud',
    audience: 'Classes',
    question: 'What\'s one word you associate with \'agile development\'?',
  },

  {
    feature: 'wordCloud',
    audience: 'Creatives',
    question:
      'In one word, describe your current creative medium\'s biggest strength.',
  },
  {
    feature: 'wordCloud',
    audience: 'Creatives',
    question: 'What\'s one word that defines the mood of your latest piece?',
  },
  {
    feature: 'wordCloud',
    audience: 'Creatives',
    question:
      'What\'s the first word that comes to mind when you hear \'storytelling\'?',
  },

  {
    feature: 'transientThought',
    audience: 'Generic',
    question: 'In a few words, share something you are grateful for.',
  },
  {
    feature: 'transientThought',
    audience: 'Generic',
    question: 'What\'s a game-changing product feature you wish we had?',
  },
  {
    feature: 'transientThought',
    audience: 'Generic',
    question: 'In a sentence, what’s a recent customer challenge you faced?',
  },
  {
    feature: 'transientThought',
    audience: 'Corporates',
    question:
      'Share a brief thought on how we could better leverage our team\'s unique skills.',
  },
  {
    feature: 'transientThought',
    audience: 'Corporates',
    question:
      'What\'s one key insight from today\'s market analysis that surprised you?',
  },
  {
    feature: 'transientThought',
    audience: 'Corporates',
    question:
      'Describe a recent team challenge you faced and how you\'d handle it differently now.',
  },

  {
    feature: 'transientThought',
    audience: 'Classes',
    question:
      'In a few sentences, explain how the \'scrum\' methodology could be applied to a non-tech project.',
  },
  {
    feature: 'transientThought',
    audience: 'Classes',
    question:
      'What\'s one a-ha moment you\'ve had in this course and what led to it?',
  },
  {
    feature: 'transientThought',
    audience: 'Classes',
    question:
      'Briefly explain a specific technique or concept you\'re most excited to apply to your work.',
  },

  {
    feature: 'transientThought',
    audience: 'Creatives',
    question:
      'What\'s one piece of advice you\'d give to someone struggling with their first commissioned work?',
  },
  {
    feature: 'transientThought',
    audience: 'Creatives',
    question:
      'Share a brief thought on how you use feedback to improve your work.',
  },
  {
    feature: 'transientThought',
    audience: 'Creatives',
    question: 'Describe the process behind your favorite piece you\'ve created.',
  },

  {
    feature: 'talkingTiles',
    audience: 'Generic',
    question: 'What\'s an AI skill you’re using to be more productive?',
  },
  {
    feature: 'talkingTiles',
    audience: 'Generic',
    question: 'Which team-member helped you last quarter, and briefly share how.',
  },
  {
    feature: 'talkingTiles',
    audience: 'Generic',
    question: 'Share one project you\'re proud of, and a key takeaway from it.',
  },
  {
    feature: 'talkingTiles',
    audience: 'Corporates',
    question: 'What\'s one KPI your team is focused on for Q3?',
  },
  {
    feature: 'talkingTiles',
    audience: 'Corporates',
    question:
      'Share a tool or app that has significantly improved your workflow this month.',
  },
  {
    feature: 'talkingTiles',
    audience: 'Corporates',
    question: 'What\'s a new skill you are working on to grow professionally?',
  },

  {
    feature: 'talkingTiles',
    audience: 'Classes',
    question:
      'Name a programming language you feel most comfortable with, and why.',
  },
  {
    feature: 'talkingTiles',
    audience: 'Classes',
    question: 'What\'s a useful shortcut or tool you\'ve learned in this course?',
  },
  {
    feature: 'talkingTiles',
    audience: 'Classes',
    question: 'Share one project you\'re proud of and a key takeaway from it.',
  },

  {
    feature: 'talkingTiles',
    audience: 'Creatives',
    question: 'What\'s your current favorite creative tool or software?',
  },
  {
    feature: 'talkingTiles',
    audience: 'Creatives',
    question: 'Share a small detail from your current creative project.',
  },
  {
    feature: 'talkingTiles',
    audience: 'Creatives',
    question: 'What\'s one creative habit you practice regularly?',
  },

  {
    feature: 'emojisEverywhere',
    audience: 'Generic',
    question: 'Use an emoji to describe how you feel after today’s session.',
  },
  {
    feature: 'emojisEverywhere',
    audience: 'Generic',
    question: 'How do you feel about these initiatives? React with an emoji.',
  },
  {
    feature: 'emojisEverywhere',
    audience: 'Generic',
    question: 'Which emoji best represents your readiness for the upcoming quarter?',
  },
  {
    feature: 'emojisEverywhere',
    audience: 'Corporates',
    question:
      'How do you feel about the new Q4 sales targets? React with an emoji.',
  },
  {
    feature: 'emojisEverywhere',
    audience: 'Corporates',
    question: 'Use an emoji to describe our team\'s current energy level.',
  },
  {
    feature: 'emojisEverywhere',
    audience: 'Corporates',
    question:
      'Which emoji best represents your readiness for the upcoming client presentation?',
  },

  {
    feature: 'emojisEverywhere',
    audience: 'Classes',
    question:
      'Show your confidence level for the next assignment with an emoji.',
  },
  {
    feature: 'emojisEverywhere',
    audience: 'Classes',
    question: 'React with an emoji: How much did you enjoy today\'s skill demo?',
  },
  {
    feature: 'emojisEverywhere',
    audience: 'Classes',
    question: 'What emoji best describes your current project\'s status?',
  },

  {
    feature: 'emojisEverywhere',
    audience: 'Creatives',
    question: 'React with an emoji: How do you feel about creative challenges?',
  },
  {
    feature: 'emojisEverywhere',
    audience: 'Creatives',
    question: 'Which emoji best captures the mood of your current artwork?',
  },
  {
    feature: 'emojisEverywhere',
    audience: 'Creatives',
    question: 'React with an emoji: How do you feel about creative challenges?',
  },
  {
    feature: 'emojisEverywhere',
    audience: 'Creatives',
    question: 'If your favorite creative tool were an emoji, what would it be?',
  },

  {
    feature: 'pollWithOptions',
    audience: 'Generic',
    question: 'What do you use AI most for?',
    options: [
      { body: 'Writing', checked: false, option_number: 1 },
      { body: 'Thinking', checked: false, option_number: 2 },
      { body: 'Visualizing', checked: false, option_number: 3 },
      { body: 'Coding', checked: false, option_number: 4 },
      { body: 'Other', checked: false, option_number: 5 },
    ],
  },
  {
    feature: 'pollWithOptions',
    audience: 'Generic',
    question: 'What\'s your biggest source of creative inspiration?',
    options: [
      { body: 'Nature', checked: false, option_number: 1 },
      { body: 'Other Art', checked: false, option_number: 2 },
      { body: 'Daily Life', checked: false, option_number: 3 },
      { body: 'Music', checked: false, option_number: 4 },
      { body: 'Other', checked: false, option_number: 5 },
    ],
  },
  {
    feature: 'pollWithOptions',
    audience: 'Generic',
    question: 'Which of these captures your familiarity with our new feature?',
    options: [
      { body: 'Never heard of it', checked: false, option_number: 1 },
      { body: 'Heard of it; never used it', checked: false, option_number: 2 },
      { body: 'Tried it; don’t use it much', checked: false, option_number: 3 },
      { body: 'Use it regularly', checked: false, option_number: 4 },
    ],
  },
  {
    feature: 'pollWithOptions',
    audience: 'Corporates',
    question: 'Which communication tool do you use most for urgent tasks?',
    options: [
      { body: 'Teams Chat', checked: false, option_number: 1 },
      { body: 'Email', checked: false, option_number: 2 },
      { body: 'Phone Call', checked: false, option_number: 3 },
      { body: 'Project Management Tool', checked: false, option_number: 4 },
    ],
  },
  {
    feature: 'pollWithOptions',
    audience: 'Corporates',
    question: 'What\'s the biggest barrier to innovation in our company?',
    options: [
      { body: 'Lack of Resources', checked: false, option_number: 1 },
      { body: 'Resistance to Change', checked: false, option_number: 2 },
      { body: 'Bureaucracy', checked: false, option_number: 3 },
      { body: 'Lack of Ideas', checked: false, option_number: 4 },
    ],
  },
  {
    feature: 'pollWithOptions',
    audience: 'Corporates',
    question: 'Which benefit is most important for work-life balance?',
    options: [
      { body: 'Flexible Hours', checked: false, option_number: 1 },
      { body: 'Remote Work', checked: false, option_number: 2 },
      { body: 'Generous PTO', checked: false, option_number: 3 },
      { body: 'Wellness Programs', checked: false, option_number: 4 },
    ],
  },

  {
    feature: 'pollWithOptions',
    audience: 'Classes',
    question: 'What\'s your preferred method for learning a new skill?',
    options: [
      { body: 'Video Tutorials', checked: false, option_number: 1 },
      { body: 'Hands-on Projects', checked: false, option_number: 2 },
      { body: 'Reading Documentation', checked: false, option_number: 3 },
      { body: 'Group Collaboration', checked: false, option_number: 4 },
    ],
  },
  {
    feature: 'pollWithOptions',
    audience: 'Classes',
    question:
      'Which programming language is most versatile for web development?',
    options: [
      { body: 'Java', checked: false, option_number: 1 },
      { body: 'Python', checked: false, option_number: 2 },
      { body: 'JavaScript', checked: false, option_number: 3 },
      { body: 'C++', checked: false, option_number: 4 },
    ],
  },
  {
    feature: 'pollWithOptions',
    audience: 'Classes',
    question: 'What type of assignment helps you learn best?',
    options: [
      { body: 'Technical Essays', checked: false, option_number: 1 },
      { body: 'Presentations', checked: false, option_number: 2 },
      { body: 'Group Projects', checked: false, option_number: 3 },
      { body: 'Coding Quizzes', checked: false, option_number: 4 },
    ],
  },

  {
    feature: 'pollWithOptions',
    audience: 'Creatives',
    question: 'What\'s your biggest creative inspiration source?',
    options: [
      { body: 'Nature', checked: false, option_number: 1 },
      { body: 'Other Art', checked: false, option_number: 2 },
      { body: 'Daily Life', checked: false, option_number: 3 },
      { body: 'Music', checked: false, option_number: 4 },
    ],
  },
  {
    feature: 'pollWithOptions',
    audience: 'Creatives',
    question: 'Which platform do you use most to showcase your work?',
    options: [
      { body: 'Instagram', checked: false, option_number: 1 },
      { body: 'Behance/ArtStation', checked: false, option_number: 2 },
      { body: 'Personal Website', checked: false, option_number: 3 },
      { body: 'YouTube/TikTok', checked: false, option_number: 4 },
    ],
  },
  {
    feature: 'pollWithOptions',
    audience: 'Creatives',
    question: 'What\'s your preferred creative medium?',
    options: [
      { body: 'Digital Art', checked: false, option_number: 1 },
      { body: 'Traditional Painting', checked: false, option_number: 2 },
      { body: 'Sculpture', checked: false, option_number: 3 },
      { body: 'Writing', checked: false, option_number: 4 },
    ],
  },

  {
    feature: 'freeForm',
    audience: 'Generic',
    question: 'What\'s one new tool you\'d like to learn in this course?',
  },
  {
    feature: 'freeForm',
    audience: 'Generic',
    question: 'What\'s a skill that you believe every leader needs?',
  },
  {
    feature: 'freeForm',
    audience: 'Generic',
    question: 'What’s something on your table that no one else might have?',
  },
  {
    feature: 'freeForm',
    audience: 'Corporates',
    question:
      'Name one thing that could make our team meetings more effective.',
  },
  {
    feature: 'freeForm',
    audience: 'Corporates',
    question: 'What\'s one skill you\'re eager to learn to boost your career?',
  },
  {
    feature: 'freeForm',
    audience: 'Corporates',
    question: 'Suggest one way we can better celebrate team successes.',
  },

  {
    feature: 'freeForm',
    audience: 'Classes',
    question:
      'What\'s one question you have about the upcoming coding assignment?',
  },
  {
    feature: 'freeForm',
    audience: 'Classes',
    question: 'Name one positive aspect of our current remote learning format.',
  },
  {
    feature: 'freeForm',
    audience: 'Classes',
    question: 'What\'s one new tool you\'d like to learn in this course?',
  },

  {
    feature: 'freeForm',
    audience: 'Creatives',
    question: 'What\'s one new technique you\'d like to try in your art?',
  },
  {
    feature: 'freeForm',
    audience: 'Creatives',
    question: 'Name one artist whose work consistently inspires you.',
  },
  {
    feature: 'freeForm',
    audience: 'Creatives',
    question:
      'What\'s one piece of feedback you\'d like on your current project?',
  },

  {
    feature: 'ratingPolls',
    audience: 'Generic',
    setting: {
      ratingPollScale: '5',
      lowScoreLabel: 'Not at all',
      highScoreLabel: 'I eat AI for breakfast',
    },
    question: 'On a scale of 1-5, how comfortable are you with using AI?',
  },
  {
    feature: 'ratingPolls',
    audience: 'Generic',
    setting: {
      ratingPollScale: '10',
      lowScoreLabel: 'Yawn',
      highScoreLabel: 'Just Wow',
    },
    question: 'On a scale of 1-10, how engaging was today\'s session?',
  },
  {
    feature: 'ratingPolls',
    audience: 'Generic',
    setting: {
      ratingPollScale: '10',
      lowScoreLabel: 'very confused',
      highScoreLabel: 'completely clear',
    },
    question: 'Rate your understanding of today’s topic from 1 to 10.',
  },
  {
    feature: 'ratingPolls',
    audience: 'Corporates',
    setting: {
      ratingPollScale: '10',
      lowScoreLabel: 'very confused',
      highScoreLabel: 'completely clear',
    },
    question:
      'On a scale of 1-10, how clear are our company\'s strategic goals?',
  },
  {
    feature: 'ratingPolls',
    audience: 'Corporates',
    setting: {
      ratingPollScale: '10',
      lowScoreLabel: 'very confused',
      highScoreLabel: 'completely clear',
    },
    question:
      'Rate the effectiveness of our current internal communication tools (1-10).',
  },
  {
    feature: 'ratingPolls',
    audience: 'Corporates',
    setting: {
      ratingPollScale: '10',
      lowScoreLabel: 'very confused',
      highScoreLabel: 'completely clear',
    },
    question:
      'How satisfied are you with your professional development opportunities (1-10)?',
  },

  {
    feature: 'ratingPolls',
    audience: 'Classes',
    setting: {
      ratingPollScale: '10',
      lowScoreLabel: 'very confused',
      highScoreLabel: 'completely clear',
    },
    question:
      'Rate your understanding of [complex concept] from 1 to 10.',
  },
  {
    feature: 'ratingPolls',
    audience: 'Classes',
    setting: {
      ratingPollScale: '10',
      lowScoreLabel: 'very confused',
      highScoreLabel: 'completely clear',
    },
    question: 'On a scale of 1-10, how engaging was today\'s lecture?',
  },
  {
    feature: 'ratingPolls',
    audience: 'Classes',
    setting: {
      ratingPollScale: '10',
      lowScoreLabel: 'too light',
      highScoreLabel: 'too heavy',
    },
    question:
      'Rate the workload for this course so far (1 to 10).',
  },

  {
    feature: 'ratingPolls',
    audience: 'Creatives',
    setting: {
      ratingPollScale: '10',
      lowScoreLabel: 'less confident',
      highScoreLabel: 'very confident',
    },
    question:
      'On a scale of 1-10, how confident are you in your current creative direction?',
  },
  {
    feature: 'ratingPolls',
    audience: 'Creatives',
    setting: {
      ratingPollScale: '10',
      lowScoreLabel: 'less satisfied',
      highScoreLabel: 'very satisfied',
    },
    question:
      'Rate your satisfaction with your current creative workspace (1-10).',
  },
  {
    feature: 'ratingPolls',
    audience: 'Creatives',
    setting: {
      ratingPollScale: '10',
      lowScoreLabel: 'less important',
      highScoreLabel: 'very important',
    },
    question:
      'How important is audience feedback to your creative process (1-10)?',
  },

  {
    feature: 'multipleChoice',
    audience: 'Generic',
    question: 'Which of these is NOT AI from Google?',
    options: [
      { body: 'NanoBanana', checked: false, option_number: 1 },
      { body: 'Gemini', checked: false, option_number: 2 },
      { body: 'Claude', checked: true, option_number: 3 },
      { body: 'Deep Mind', checked: false, option_number: 4 },
    ],
  },
  {
    feature: 'multipleChoice',
    audience: 'Generic',
    question: 'Which of these is NOT a presentation software?',
    options: [
      { body: 'PowerPoint', checked: false, option_number: 1 },
      { body: 'Gamma', checked: false, option_number: 2 },
      { body: 'Slideroo', checked: true, option_number: 3 },
      { body: 'Canva', checked: false, option_number: 4 },
    ],
  },
  {
    feature: 'multipleChoice',
    audience: 'Generic',
    question: 'Which color is complementary to blue on the color wheel?',
    options: [
      { body: 'Red', checked: false, option_number: 1 },
      { body: 'Yellow', checked: false, option_number: 2 },
      { body: 'Orange', checked: true, option_number: 3 },
      { body: 'Green', checked: false, option_number: 4 },
    ],
  },
  {
    feature: 'multipleChoice',
    audience: 'Corporates',
    question: 'Which of these is NOT a core value of our company?',
    options: [
      { body: 'Innovation', checked: false, option_number: 1 },
      { body: 'Integrity', checked: false, option_number: 2 },
      { body: 'Fictional Value', checked: true, option_number: 3 },
      { body: 'Customer Focus', checked: false, option_number: 4 },
    ],
  },
  {
    feature: 'multipleChoice',
    audience: 'Corporates',
    question:
      'According to our HR policy, how many days\' notice is required for resignation?',
    options: [
      { body: '7', checked: false, option_number: 1 },
      { body: '14', checked: false, option_number: 2 },
      { body: '30', checked: true, option_number: 3 },
      { body: '60', checked: false, option_number: 4 },
    ],
  },
  {
    feature: 'multipleChoice',
    audience: 'Corporates',
    question:
      'Which software is primarily used for tracking project deadlines and team tasks?',
    options: [
      { body: 'Salesforce', checked: false, option_number: 1 },
      { body: 'Zendesk', checked: false, option_number: 2 },
      { body: 'Jira', checked: true, option_number: 3 },
      { body: 'Dropbox', checked: false, option_number: 4 },
    ],
  },

  {
    feature: 'multipleChoice',
    audience: 'Classes',
    question: 'Which of these is a Python data structure?',
    options: [
      { body: 'Class', checked: false, option_number: 1 },
      { body: 'Function', checked: false, option_number: 2 },
      { body: 'Tuple', checked: true, option_number: 3 },
      { body: 'Loop', checked: false, option_number: 4 },
    ],
  },
  {
    feature: 'multipleChoice',
    audience: 'Classes',
    question:
      'Which agile principle emphasizes adaptability over strict planning?',
    options: [
      { body: 'Face-to-face conversation', checked: false, option_number: 1 },
      { body: 'Sustainable development', checked: false, option_number: 2 },
      { body: 'Responding to change', checked: true, option_number: 3 },
      {
        body: 'Continuous attention to quality',
        checked: false,
        option_number: 4,
      },
    ],
  },
  {
    feature: 'multipleChoice',
    audience: 'Classes',
    question: 'What is the first step in a data analysis workflow?',
    options: [
      { body: 'Modeling', checked: false, option_number: 1 },
      { body: 'Data Cleaning', checked: false, option_number: 2 },
      { body: 'Visualization', checked: false, option_number: 3 },
      { body: 'Data Collection', checked: true, option_number: 4 },
    ],
  },

  {
    feature: 'multipleChoice',
    audience: 'Creatives',
    question: 'Which color is complementary to blue on the color wheel?',
    options: [
      { body: 'Red', checked: false, option_number: 1 },
      { body: 'Yellow', checked: false, option_number: 2 },
      { body: 'Orange', checked: true, option_number: 3 },
      { body: 'Green', checked: false, option_number: 4 },
    ],
  },
  {
    feature: 'multipleChoice',
    audience: 'Creatives',
    question:
      'What term describes a rapid succession of images to create apparent motion?',
    options: [
      { body: 'Sculpture', checked: false, option_number: 1 },
      { body: 'Still Life', checked: false, option_number: 2 },
      { body: 'Animation', checked: true, option_number: 3 },
      { body: 'Portrait', checked: false, option_number: 4 },
    ],
  },
  {
    feature: 'multipleChoice',
    audience: 'Creatives',
    question:
      'Which of these is a common element of composition in visual art?',
    options: [
      { body: 'Melody', checked: false, option_number: 1 },
      { body: 'Rhythm', checked: true, option_number: 2 },
      { body: 'Harmony', checked: false, option_number: 3 },
      { body: 'Texture', checked: false, option_number: 4 },
    ],
  },

  {
    feature: 'wheel',
    audience: 'Generic',
    question: 'What AI task shall we do next?',
    options: [
      { body: 'Writing a LinkedIn Post', checked: false, option_number: 1 },
      { body: 'Creating an infographic', checked: false, option_number: 2 },
      { body: 'Researching something', checked: false, option_number: 3 },
      { body: 'Creating an avatar', checked: false, option_number: 4 },
      { body: 'Making a simple game', checked: false, option_number: 5 },
    ],
  },
  {
    feature: 'wheel',
    audience: 'Generic',
    question: 'Which team presents next?',
    options: [
      { body: 'Team Blue', checked: false, option_number: 1 },
      { body: 'Team Red', checked: false, option_number: 2 },
      { body: 'Team Green', checked: false, option_number: 3 },
      { body: 'Team Orange', checked: false, option_number: 4 },
    ],
  },
  {
    feature: 'wheel',
    audience: 'Generic',
    question: 'What shall we do for our next team-building activity?',
    options: [
      { body: 'Virtual Escape Room', checked: false, option_number: 1 },
      { body: 'Office Trivia & Pizza', checked: false, option_number: 2 },
      { body: 'Local Park Cleanup', checked: false, option_number: 3 },
      { body: 'Online Cooking Class', checked: false, option_number: 4 },
      { body: 'Volunteer at a Food Bank', checked: false, option_number: 5 },
    ],
  },
  {
    feature: 'wheel',
    audience: 'Corporates',
    question: 'What\'s our next team-building activity?',
    options: [
      { body: 'Virtual Escape Room', checked: false, option_number: 1 },
      { body: 'Office Trivia & Pizza', checked: false, option_number: 2 },
      { body: 'Local Park Cleanup', checked: false, option_number: 3 },
      { body: 'Online Cooking Class', checked: false, option_number: 4 },
      { body: 'Volunteer at a Food Bank', checked: false, option_number: 5 },
    ],
  },
  {
    feature: 'wheel',
    audience: 'Corporates',
    question: 'Which topic for our next all-hands meeting?',
    options: [
      { body: 'Q3 Product Roadmap', checked: false, option_number: 1 },
      { body: 'Client Success Stories', checked: false, option_number: 2 },
      { body: 'DEI Initiatives & Progress', checked: false, option_number: 3 },
      {
        body: 'Budgeting for the New Fiscal Year',
        checked: false,
        option_number: 4,
      },
      {
        body: 'Our New Sustainability Goals',
        checked: false,
        option_number: 5,
      },
      { body: 'The Future of Remote Work', checked: false, option_number: 6 },
    ],
  },
  {
    feature: 'wheel',
    audience: 'Corporates',
    question: 'Who will lead the next project update?',
    options: [
      { body: 'Marketing Team Lead', checked: false, option_number: 1 },
      { body: 'Product Manager', checked: false, option_number: 2 },
      { body: 'Senior Engineer', checked: false, option_number: 3 },
      { body: 'Sales Director', checked: false, option_number: 4 },
      { body: 'Head of Customer Support', checked: false, option_number: 5 },
    ],
  },

  {
    feature: 'wheel',
    audience: 'Classes',
    question: 'Which coding challenge will we attempt first?',
    options: [
      { body: 'Build a Simple Calculator', checked: false, option_number: 1 },
      { body: 'Implement a To-Do List App', checked: false, option_number: 2 },
      { body: 'Recreate the Snake Game', checked: false, option_number: 3 },
      { body: 'Fetch Data from an API', checked: false, option_number: 4 },
      {
        body: 'Design a Basic Website Layout',
        checked: false,
        option_number: 5,
      },
    ],
  },
  {
    feature: 'wheel',
    audience: 'Classes',
    question: 'What\'s our next class debate topic?',
    options: [
      {
        body: 'Is AI a Threat to Creativity?',
        checked: false,
        option_number: 1,
      },
      { body: 'The Ethics of Big Data', checked: false, option_number: 2 },
      {
        body: 'Should Tech Companies Be More Regulated?',
        checked: false,
        option_number: 3,
      },
      {
        body: 'The Future of Sustainable Energy',
        checked: false,
        option_number: 4,
      },
      {
        body: 'The Impact of Social Media on Society',
        checked: false,
        option_number: 5,
      },
      {
        body: 'The Importance of Open Source Software',
        checked: false,
        option_number: 6,
      },
    ],
  },
  {
    feature: 'wheel',
    audience: 'Classes',
    question: 'Which student will present their coding project first?',
    options: [
      { body: 'Student A', checked: false, option_number: 1 },
      { body: 'Student B', checked: false, option_number: 2 },
      { body: 'Student C', checked: false, option_number: 3 },
      { body: 'Student D', checked: false, option_number: 4 },
      { body: 'Student E', checked: false, option_number: 5 },
      { body: 'Student F', checked: false, option_number: 6 },
      { body: 'Student G', checked: false, option_number: 7 },
      { body: 'Student H', checked: false, option_number: 8 },
    ],
  },

  {
    feature: 'wheel',
    audience: 'Creatives',
    question: 'What\'s the theme for our next collaborative art piece?',
    options: [
      { body: 'Cyberpunk Cityscape', checked: false, option_number: 1 },
      { body: 'Mystical Forest', checked: false, option_number: 2 },
      { body: 'Retro-Futurism', checked: false, option_number: 3 },
      { body: 'Underwater Life', checked: false, option_number: 4 },
      { body: 'Desert Landscape', checked: false, option_number: 5 },
    ],
  },
  {
    feature: 'wheel',
    audience: 'Creatives',
    question: 'Which color palette should we explore today?',
    options: [
      { body: 'Warm Tones (Reds & Oranges)', checked: false, option_number: 1 },
      { body: 'Cool Tones (Blues & Greens)', checked: false, option_number: 2 },
      { body: 'Pastel Dream', checked: false, option_number: 3 },
      { body: 'Monochromatic Grays', checked: false, option_number: 4 },
      { body: 'Neon & Brights', checked: false, option_number: 5 },
      { body: 'Earthy & Neutral', checked: false, option_number: 6 },
    ],
  },
  {
    feature: 'wheel',
    audience: 'Creatives',
    question: 'What\'s our next creative challenge?',
    options: [
      { body: '30-Second Animation', checked: false, option_number: 1 },
      { body: 'Re-design a Book Cover', checked: false, option_number: 2 },
      {
        body: 'Create a Character in a New Style',
        checked: false,
        option_number: 3,
      },
      { body: 'Design a Fictional Product', checked: false, option_number: 4 },
      { body: 'Illustrate a Short Poem', checked: false, option_number: 5 },
      {
        body: 'Create a Mood Board from Scratch',
        checked: false,
        option_number: 6,
      },
      {
        body: 'Storyboard a Scene from a Film',
        checked: false,
        option_number: 7,
      },
    ],
  },
];

const buckets = {
  'Learning & Development': 'Corporates',
  Training: 'Corporates',
  'Group Coaching': 'Corporates',
  'Town Halls': 'Corporates',
  'All Hands/Large Meetings': 'Corporates',
  Webinars: 'Corporates',
  'Events & Conferences': 'Corporates',
  Education: 'Classes',
  Gaming: 'Creatives',
  Livestreaming: 'Creatives',
  Events: 'Creatives',
  Generic: 'Generic',
};

const getBucket = (audience: string) =>
  buckets[audience as keyof typeof buckets] || 'Generic';

export const getQuestionsByCategoryAndSub = (
  feature: InteractionType,
  session: string[] = ['Generic']
): QuestionSuggestion[] => {
  const tempSession = session.length ? session : ['Generic'];
  const buckets = Array.from(new Set(tempSession.map(getBucket)));

  return shuffle(
    questionSuggestions.filter(
      (q) => q.feature === feature && buckets.includes(q.audience)
    )
  ).slice(0, 3);
};
